import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Leaf, Wind, Sparkles, Play, Pause, RotateCcw } from "lucide-react";
import { DAILY_QUOTES } from "@/data/sessions";
import { motion } from "framer-motion";

// Rotate quote every 6h — deterministic based on current 6h bucket
function getCurrentQuote() {
  const bucket = Math.floor(Date.now() / (6 * 60 * 60 * 1000));
  return DAILY_QUOTES[bucket % DAILY_QUOTES.length];
}
function getNextQuoteInMs() {
  const bucket = 6 * 60 * 60 * 1000;
  return bucket - (Date.now() % bucket);
}

const BREATHING = [
  {
    id: "4-4-4-4",
    name: "Respiración abdominal 4-4-4-4",
    phases: [
      { label: "Inhala", seconds: 4, color: "var(--ha-primary)" },
      { label: "Retén", seconds: 4, color: "var(--ha-secondary)" },
      { label: "Exhala", seconds: 4, color: "var(--ha-accent)" },
      { label: "Retén", seconds: 4, color: "var(--ha-secondary)" },
    ],
    rounds: 10,
    description: "Inhala 4s, retén 4s, exhala 4s, retén 4s. Repite 10 veces.",
  },
  {
    id: "muscular",
    name: "Relajación muscular progresiva",
    phases: [
      { label: "Tensa", seconds: 5, color: "var(--ha-secondary)" },
      { label: "Suelta", seconds: 10, color: "var(--ha-primary)" },
    ],
    rounds: 8,
    description: "Recorre desde los pies hasta la cara: 5s de tensión, 10s de relajación.",
  },
  {
    id: "alterna",
    name: "Respiración alterna nasal",
    phases: [
      { label: "Inhala izq.", seconds: 4, color: "var(--ha-primary)" },
      { label: "Exhala der.", seconds: 6, color: "var(--ha-accent)" },
      { label: "Inhala der.", seconds: 4, color: "var(--ha-primary)" },
      { label: "Exhala izq.", seconds: 6, color: "var(--ha-accent)" },
    ],
    rounds: 8,
    description: "Alterna las fosas nasales durante 5-10 minutos.",
  },
  {
    id: "autogena",
    name: "Relajación autógena",
    phases: [
      { label: "Brazo pesado", seconds: 15, color: "var(--ha-primary)" },
      { label: "Brazo cálido", seconds: 15, color: "var(--ha-secondary)" },
    ],
    rounds: 6,
    description: "Concéntrate en pesadez y calor por partes del cuerpo.",
  },
];

function BreathingTimer({ technique }) {
  const [running, setRunning] = useState(false);
  const [round, setRound] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [seconds, setSeconds] = useState(technique.phases[0].seconds);
  const phaseIdxRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => { phaseIdxRef.current = phaseIdx; }, [phaseIdx]);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSeconds(s => {
        if (s > 1) return s - 1;
        const currentP = phaseIdxRef.current;
        const nextP = (currentP + 1) % technique.phases.length;
        setPhaseIdx(nextP);
        if (nextP === 0) {
          setRound(r => {
            const nr = r + 1;
            if (nr >= technique.rounds) {
              setRunning(false);
              setSeconds(technique.phases[0].seconds);
              setPhaseIdx(0);
              return 0;
            }
            return nr;
          });
        }
        return technique.phases[nextP].seconds;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, technique]);

  const currentPhase = technique.phases[phaseIdx];
  const totalSecondsInPhase = currentPhase.seconds;
  const progress = ((totalSecondsInPhase - seconds + 1) / totalSecondsInPhase) * 100;

  const reset = () => {
    setRunning(false);
    setRound(0);
    setPhaseIdx(0);
    setSeconds(technique.phases[0].seconds);
  };

  return (
    <div className="mt-4 rounded-2xl border border-[var(--ha-border)] bg-[var(--ha-bg)] p-6" data-testid={`breathing-timer-${technique.id}`}>
      <div className="relative h-40 w-40 mx-auto grid place-items-center">
        <svg className="absolute inset-0" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="var(--ha-border)" strokeWidth="4" />
          <motion.circle
            cx="50" cy="50" r="45" fill="none"
            stroke={currentPhase.color}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 45}
            initial={{ strokeDashoffset: 2 * Math.PI * 45 }}
            animate={{ strokeDashoffset: 2 * Math.PI * 45 * (1 - progress / 100) }}
            transition={{ duration: 1, ease: "linear" }}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div className="text-center relative">
          <p className="text-xs uppercase tracking-[0.14em] font-bold" style={{ color: currentPhase.color }}>{currentPhase.label}</p>
          <p className="text-4xl font-extrabold text-[var(--ha-text)] mt-1">{seconds}</p>
          <p className="text-xs text-[var(--ha-muted)] mt-1">Ronda {round + 1}/{technique.rounds}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-center gap-3">
        <Button data-testid={`play-${technique.id}`} onClick={() => setRunning(r => !r)} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-10 px-5">
          {running ? <><Pause className="w-4 h-4 mr-1.5" strokeWidth={2} /> Pausar</> : <><Play className="w-4 h-4 mr-1.5" strokeWidth={2} /> Comenzar</>}
        </Button>
        <Button data-testid={`reset-${technique.id}`} variant="outline" onClick={reset} className="rounded-full border-[var(--ha-border)] h-10 px-4">
          <RotateCcw className="w-4 h-4" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}

export default function Anexos() {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(getCurrentQuote());
  const [active, setActive] = useState(null);

  useEffect(() => {
    const nextMs = getNextQuoteInMs();
    const t = setTimeout(() => setQuote(getCurrentQuote()), nextMs);
    return () => clearTimeout(t);
  }, [quote]);

  return (
    <main className="min-h-screen bg-[var(--ha-bg)]" data-testid="anexos-page">
      <header className="max-w-5xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-full bg-[var(--ha-primary)] grid place-items-center shadow-sm">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold tracking-tight text-[var(--ha-text)] text-lg">MINDHOME HEALTH APP</span>
        </div>
        <Button data-testid="back-dashboard-btn" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full border-[var(--ha-border)] h-9 px-4">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Volver al panel
        </Button>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--ha-muted)] font-semibold">Anexos de bienestar</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ha-text)]">Recursos diarios de cuidado</h1>

        {/* Daily quote */}
        <Card className="mt-8 rounded-3xl border-[var(--ha-secondary)]/30 bg-gradient-to-br from-[var(--ha-secondary)]/10 to-[var(--ha-primary)]/5" data-testid="daily-quote-card">
          <CardContent className="p-8 sm:p-10">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[var(--ha-secondary)]" strokeWidth={1.75} />
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Frase de hoy · cambia cada 6h</p>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)] leading-snug italic" data-testid="daily-quote-text">"{quote}"</p>
          </CardContent>
        </Card>

        {/* Breathing techniques */}
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Wind className="w-5 h-5 text-[var(--ha-primary)]" strokeWidth={1.75} />
            <h2 className="text-2xl font-bold text-[var(--ha-text)]">Técnicas respiratorias guiadas</h2>
          </div>
          <p className="text-[var(--ha-muted)]">Elige una técnica y pulsa "Comenzar" para que el timer te guíe fase por fase.</p>

          <div className="mt-6 grid md:grid-cols-2 gap-5">
            {BREATHING.map(t => (
              <Card key={t.id} className="rounded-2xl border-[var(--ha-border)] bg-white" data-testid={`technique-card-${t.id}`}>
                <CardContent className="p-6">
                  <h3 className="font-bold text-[var(--ha-text)]">{t.name}</h3>
                  <p className="mt-2 text-sm text-[var(--ha-muted)] leading-relaxed">{t.description}</p>
                  {active === t.id ? (
                    <BreathingTimer technique={t} />
                  ) : (
                    <Button
                      data-testid={`open-technique-${t.id}`}
                      onClick={() => setActive(t.id)}
                      className="mt-4 rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-10 px-5"
                    >
                      <Play className="w-4 h-4 mr-1.5" strokeWidth={2} /> Abrir guía
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
